// File: typescript/apps/front-end/src/main/react/group/invite/useGroupInviteLanding.ts
//
// The group page's door onto the invite link landing (chat completeness §F18). Deliberately tiny: the group
// page may sit on the startup path, so this only reads the URL and a one-shot flash, and loads the landing
// itself (`groupInviteLanding.ts`) only when the URL carries a code (rule 26).
//
// The `groupInvite` param is an instruction, not a state, so it is stripped as a `consume-one-shot` (a
// reload or Back must not redeem it again). `sv_s` is left alone: first-touch attribution reads it.

import React from "react";
import { toast } from "sonner";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import { replaceInPageHistoryUrl } from "@/main/navigation/NavigationHistoryState";
import type { Localizer } from "@/main/react/Types";

export const GROUP_INVITE_PARAM = "groupInvite";
/** One-shot "you joined" flash, carried across the reload a private group's wall needs after a join. */
export const GROUP_INVITE_JOINED_FLASH_KEY = "sv.groupInviteJoined";

/** Read and strip the invite code from the address bar. Null when there is none. */
export function consumeGroupInviteCode(): string | null {
    if (typeof window === "undefined") {
        return null;
    }
    const url = new URL(window.location.href);
    const code = url.searchParams.get(GROUP_INVITE_PARAM)?.trim();
    if (!code) {
        return null;
    }
    url.searchParams.delete(GROUP_INVITE_PARAM);
    replaceInPageHistoryUrl(`${url.pathname}${url.search}${url.hash}`, "consume-one-shot");
    return code;
}

function showJoinedFlash(groupPublicId: string, localizer: Localizer) {
    try {
        const raw = window.sessionStorage.getItem(GROUP_INVITE_JOINED_FLASH_KEY);
        if (!raw) {
            return;
        }
        window.sessionStorage.removeItem(GROUP_INVITE_JOINED_FLASH_KEY);
        const flash = JSON.parse(raw) as { groupPublicId?: string; groupName?: string };
        if (flash?.groupPublicId === groupPublicId) {
            toast.success(localizer("group.invite_link.joined", flash.groupName ?? ""));
        }
    } catch {
        // Storage unavailable or a malformed value: the flash is a courtesy, never a failure.
    }
}

export function useGroupInviteLanding(args: {
    groupPublicId: string;
    groupSlug: string;
    groupName?: string;
    deniedAccess?: boolean;
    localizer: Localizer;
}): void {
    const handledRef = React.useRef(false);
    React.useEffect(() => {
        if (handledRef.current || typeof window === "undefined" || !args.groupPublicId) {
            return;
        }
        handledRef.current = true;
        showJoinedFlash(args.groupPublicId, args.localizer);
        const code = consumeGroupInviteCode();
        if (!code) {
            return;
        }
        void importChunkWithRetryAndReload(
            () => import("@/main/react/group/invite/groupInviteLanding"),
            "group-invite-landing",
        ).then((module) => module.default({ ...args, code }));
        // Runs once per page instance: the code is consumed on the first pass.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
